import { OverlayModule } from '@angular/cdk/overlay';

import {
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	forwardRef,
	inject,
	input,
	signal,
	ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptgroup } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	PERSON_TYPE,
	personTypeTranslationKeys,
	SearchQuery,
	SearchResultItem,
} from '../../../api/app-types/search.app';
import { SearchService } from '../../../api/services/search.service';
import { LvrField } from '../../../core/components/field/field';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { ParticipantItem } from './participant-item';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { ContactType } from '../../../api/app-types/contact-type';
import { ContactPointType } from '../../../api/app-types/contact-point.app';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { SendEmailButton } from '../../../core/components/send-email-button/send-email-button';
import { LOADING, provideLoading } from '../../../core/services/loading.service';

const SEARCH_LIMIT = 50;

// Request key for the async client lookup triggered by the "client participated" toggle.
// Used to expose a pending state so consumers can block saving until it settles.
const CLIENT_PARTICIPANT_LOOKUP_REQUEST_KEY = 'participants.toggleClientAsParticipant';

@Component({
	selector: 'lvr-participants',
	templateUrl: './participants.html',
	styleUrl: './participants.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatInputModule,
		MatCheckboxModule,
		ReactiveFormsModule,
		MatOptgroup,
		OverlayModule,
		TranslocoPipe,
		ParticipantItem,
		LvrPaddingDirective,
		LvrField,
		CdkAccordionModule,
		MatAutocompleteModule,
		SendEmailButton,
	],
	providers: [
		SearchService,
		provideLoading,
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => Participants),
			multi: true,
		},
	],
})
export class Participants implements ControlValueAccessor {
	@ViewChild('participantInput', { read: ElementRef }) private participantInput?: ElementRef<HTMLInputElement>;

	private readonly search = inject(SearchService);
	private readonly loading = inject(LOADING);
	/**
	 * Whether the client lookup triggered by the "client participated" toggle is still in
	 * flight. Consumers (e.g. save buttons) should stay disabled while this is true, since the
	 * client isn't in `participants` yet.
	 */
	public readonly isClientLookupPending = this.loading.isCurrentlyLoading(
		CLIENT_PARTICIPANT_LOOKUP_REQUEST_KEY,
		'requestKey',
	);
	public readonly clientId = input<number>();
	public readonly clientFullName = input<string>();
	public readonly label = input.required<string>();
	public readonly showEmailSendButton = input<boolean>(false);
	public readonly subject = input<string | null | undefined>('');
	public readonly body = input<string | null | undefined>('');
	public readonly typeOfContact = input<ContactType | null | undefined>(undefined);
	public readonly searchUsers = input(true);
	public readonly searchInstitutionPerson = input(true);
	public readonly allowedUserObjectIds = input<string[] | undefined>(undefined);
	public readonly useStaticResults = input(false);
	public readonly staticResults = input<SearchResultItem[] | undefined>(undefined);
	public readonly singleSelect = input(false);
	public readonly showClientToggle = input(true);
	public readonly showCheckbox = input(true);
	public readonly userSpecializations = input<Record<string, string> | undefined>(undefined);
	public readonly selectedLabelKey = computed(() => {
		if (
			this.singleSelect() &&
			!this.showCheckbox() &&
			!this.showClientToggle() &&
			!this.searchUsers() &&
			!this.searchInstitutionPerson() &&
			this.useStaticResults()
		) {
			return 'base.field.selected_specialist';
		}
		return 'base.field.selected_participants';
	});
	public readonly noSelectionLabelKey = computed(() => {
		if (
			this.singleSelect() &&
			!this.showCheckbox() &&
			!this.showClientToggle() &&
			!this.searchUsers() &&
			!this.searchInstitutionPerson() &&
			this.useStaticResults()
		) {
			return 'base.field.no_specialist_selected';
		}
		return 'base.field.no_participants';
	});
	public readonly searchTerm = signal<string>('');
	public readonly searchResults = signal<SearchResultItem[]>([]);
	public readonly searching = signal<boolean>(false);
	public readonly resultsGrouped = computed(() => {
		// group search result items by personType
		const groups = new Map<PERSON_TYPE, (SearchResultItem & { selected: boolean })[]>();
		for (const item of this.searchResults()) {
			const group = item.personType;
			if (!groups.has(group)) {
				groups.set(group, []);
			}
			const found = this.value()?.find((i) => {
				return i.item.id === item.item.id && i.personType === item.personType;
			});
			groups.get(group)!.push({ selected: !!found, ...item });
		}
		return groups;
	});

	// Form control value
	private readonly value = signal<SearchResultItem[]>([]);
	private timeoutId?: number;
	public readonly isDisabled = signal<boolean>(false);
	public readonly isClientParticipant = computed(() =>
		this.value().some((v) => v.item.id === this.clientId() && v.personType === PERSON_TYPE.CLIENT),
	);
	public readonly prefetchedResults = signal<SearchResultItem[]>([]);

	public get selected() {
		return this.value() ?? [];
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	public onFocus() {}

	public translatePersonType(type: number) {
		return personTypeTranslationKeys[type as PERSON_TYPE] || 'personType.unknown';
	}

	// ControlValueAccessor callbacks
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onChange = (_value: SearchResultItem[] | null) => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onTouched = () => {};

	public constructor() {
		effect(() => {
			if (!this.useStaticResults()) return;
			const initial = this.applyStaticResults('');
			this.prefetchedResults.set(initial);
			if (this.searchTerm().trim().length === 0) {
				this.searchResults.set(initial);
			}
		});

		effect(() => {
			if (!this.useStaticResults()) return;
			const initial = this.applyStaticResults('');
			this.prefetchedResults.set(initial);
			if (this.searchTerm().trim().length === 0) {
				this.searchResults.set(initial);
			}
		});

		afterNextRender(async () => {
			this.searching.set(true);
			try {
				if (this.useStaticResults()) {
					const initial = this.applyStaticResults('');
					this.prefetchedResults.set(initial);
					this.searchResults.set(initial);
				} else {
					await this.fetchSearchResults('', this.clientId());
					this.prefetchedResults.set(this.searchResults());
				}
				if (this.useStaticResults()) {
					const initial = this.applyStaticResults('');
					this.prefetchedResults.set(initial);
					this.searchResults.set(initial);
				} else {
					await this.fetchSearchResults('', this.clientId());
					this.prefetchedResults.set(this.searchResults());
				}
			} finally {
				this.searching.set(false);
			}
		});

		effect(async () => {
			const term = this.searchTerm().trim();
			if (term.length === 0) {
				// Returns prefetched results
				this.searchResults.set(this.prefetchedResults());
				return;
			}

			if (term.length < 3) return;

			this.searching.set(true);
			try {
				if (this.useStaticResults()) {
					this.searchResults.set(this.applyStaticResults(term));
				} else {
					await this.fetchSearchResults(term, this.clientId());
				}
				if (this.useStaticResults()) {
					this.searchResults.set(this.applyStaticResults(term));
				} else {
					await this.fetchSearchResults(term, this.clientId());
				}
			} finally {
				this.searching.set(false);
			}
		});
	}

	public onBlur() {
		this.onTouched();
	}

	// ControlValueAccessor implementation
	public writeValue(value: SearchResultItem[] | null) {
		this.value.set(value ?? []);
	}

	public onSelect(value: SearchResultItem & { selected: boolean }) {
		if (value.selected) this.deselectValue(value);
		else this.selectValue(value);
	}

	public selectValue(value: SearchResultItem) {
		const vals = (this.value() ?? []).filter((i) => i.item.id !== value.item.id || i.personType !== value.personType);
		if (this.singleSelect()) {
			this.value.set([value]);
		} else {
			this.value.set([...vals, value]);
		}
		if (this.singleSelect()) {
			this.value.set([value]);
		} else {
			this.value.set([...vals, value]);
		}
		this.onChange(this.value());
	}

	public deselectValue(value: SearchResultItem) {
		const vals = (this.value() ?? []).filter((i) => i.item.id !== value.item.id || i.personType !== value.personType);
		this.value.set([...vals]);
		this.onChange(this.value());
	}

	public registerOnChange(fn: (value: SearchResultItem[] | null) => void) {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean) {
		this.isDisabled.set(isDisabled);
	}

	public updateTerm(event: Event) {
		clearTimeout(this.timeoutId);
		this.timeoutId = window.setTimeout(() => {
			this.searchTerm.set(event.target ? (event.target as HTMLInputElement).value : '');
		}, 300);
	}

	public trackItem(item: SearchResultItem) {
		const trackBy = item.item.id + '-' + item.personType;
		return trackBy;
	}

	/**
	 * Filters contact point by emails and returns an email list
	 */
	public emails(participant: SearchResultItem) {
		if (!participant.contactPoints?.length) return [];

		return participant.contactPoints.filter((cp) => cp.categoryContactPointId === ContactPointType.EMAIL);
	}

	/**
	 * Fetches a list of possible participants for a client based on a given search term.
	 * @param term The actual search term
	 * @param clientId ID of the client for which we want to search for participants
	 * @returns A list of search results
	 */
	private async fetchSearchResults(term: string, clientId?: number) {
		const query: SearchQuery = {
			searchTerm: term ?? '',
			searchClientEnvironments: true,
			clientId,
			searchClients: false,
			searchUsers: this.searchUsers(),
			searchInstitutions: false,
			searchInstitutionBranches: false,
			searchInstitutionPerson: this.searchInstitutionPerson(),
			searchSponsors: false,
			searchSponsorPerson: true,
			searchIso: false,
			range: SEARCH_LIMIT,
			includeContactPoints: true,
		};

		const results = await this.search.search(query).then((result) => result.entities);
		this.searchResults.set(this.filterAllowed(results));
	}

	private applyStaticResults(term: string) {
		const results = this.filterAllowed(this.staticResults() ?? []);
		if (!term) return results;
		const query = term.toLowerCase();
		return results.filter((result) => {
			const name = `${result.item.firstName ?? ''} ${result.item.name ?? ''}`.toLowerCase();
			return name.includes(query);
		});
	}

	private filterAllowed(results: SearchResultItem[]) {
		const allowed = this.allowedUserObjectIds();
		if (!allowed || allowed.length === 0) return results;
		return results.filter((r) => r.personType === PERSON_TYPE.USER && allowed.includes(r.item.objectId));
	}

	public async toggleClientAsParticipant(): Promise<void> {
		if (!this.showClientToggle()) return;
		const clientId = this.clientId();
		const query: SearchQuery = {
			searchTerm: this.clientFullName(),
			searchClientEnvironments: false,
			clientId,
			searchClients: true,
			searchUsers: false,
			searchInstitutions: false,
			searchInstitutionBranches: false,
			searchInstitutionPerson: false,
			searchSponsors: false,
			searchSponsorPerson: false,
			searchIso: false,
			includeContactPoints: true,
			range: SEARCH_LIMIT,
		};

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), CLIENT_PARTICIPANT_LOOKUP_REQUEST_KEY);
		try {
			const { entities } = await this.search.search(query);
			const match = entities.find((e) => e.item.id === clientId && e.personType === PERSON_TYPE.CLIENT);

			if (!match) return;

			const shouldSelect = !this.isClientParticipant();
			if (shouldSelect) {
				this.selectValue(match);
			} else {
				this.deselectValue(match);
			}
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public onOptionSelected(event: MatAutocompleteSelectedEvent) {
		const selected = event.option.value as SearchResultItem;

		const found = this.searchResults().find(
			(r) => r.item.id === selected.item.id && r.personType === selected.personType,
		);
		if (!found) return;

		const alreadySelected = this.value().some(
			(v) => v.item.id === selected.item.id && v.personType === selected.personType,
		);

		this.onSelect({ ...found, selected: alreadySelected });

		// Clean input
		const input = this.participantInput?.nativeElement;
		if (input) input.value = '';

		// Clean search term
		this.searchTerm.set('');
	}

	public specializationFor(option: SearchResultItem) {
		if (option.personType !== PERSON_TYPE.USER) return '';
		const map = this.userSpecializations();
		if (!map) return '';
		return map[option.item.objectId] ?? '';
	}

	public isContactTypeAnEmail() {
		const remark = this.typeOfContact()?.remark?.toLowerCase();
		return remark == 'email' || remark == 'e-mail';
	}
}
